/**
 * Auth-Store (Pinia) — Token-Verwaltung + User-State.
 * Abgeleitet aus BuildScan (src/core/auth/auth.store.ts).
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  login as apiLogin,
  refreshToken as apiRefresh,
  istTokenResponse,
  type LoginRequest,
  type UserResponse,
  type TokenResponse,
  type MfaStatus,
} from './auth.service';
import { sessionStartSetzen, sessionStartLoeschen } from '@core/composables/useAutoLogout';

const TOKEN_KEY = 'bb_access_token';
const REFRESH_KEY = 'bb_refresh_token';
const USER_KEY = 'bb_user';

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(localStorage.getItem(TOKEN_KEY));
  const refreshTokenVal = ref<string | null>(localStorage.getItem(REFRESH_KEY));
  const user = ref<UserResponse | null>(JSON.parse(localStorage.getItem(USER_KEY) || 'null'));
  const error = ref<string | null>(null);
  const loading = ref(false);

  const isLoggedIn = computed(() => !!accessToken.value && !!user.value);
  const istAdmin = computed(() => user.value?.rolle === 'admin');
  /** Startpasswort aktiv — Router leitet zwingend auf /passwort-aendern. */
  const mussPasswortAendern = computed(() => !!user.value?.must_change_password);

  function setTokens(access: string, refresh: string, userData: UserResponse) {
    accessToken.value = access;
    refreshTokenVal.value = refresh;
    user.value = userData;
    localStorage.setItem(TOKEN_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  }

  function clearTokens() {
    accessToken.value = null;
    refreshTokenVal.value = null;
    user.value = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    sessionStartLoeschen();
  }

  /** Tokens einer erfolgreichen (ggf. 2FA-)Anmeldung übernehmen.
   *  Setzt den Sitzungsstart für die 60-Min-Obergrenze (nur beim Login, nicht
   *  beim stillen Refresh — der ruft setTokens direkt auf). */
  function applyTokens(res: TokenResponse) {
    setTokens(res.access_token, res.refresh_token, res.user);
    sessionStartSetzen();
  }

  /**
   * Schritt 1: Passwort-Login. Ergebnis:
   * - { done: true }              → eingeloggt (2FA aus)
   * - { done: false, status, mfaToken } → 2FA-Schritt nötig
   */
  async function login(data: LoginRequest): Promise<{ done: boolean; status?: MfaStatus; mfaToken?: string }> {
    loading.value = true;
    error.value = null;
    try {
      const res = await apiLogin(data);
      if (istTokenResponse(res)) {
        applyTokens(res);
        return { done: true };
      }
      return { done: false, status: res.status, mfaToken: res.mfa_token };
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function refresh() {
    if (!refreshTokenVal.value) {
      clearTokens();
      return;
    }
    try {
      const res = await apiRefresh(refreshTokenVal.value);
      setTokens(res.access_token, res.refresh_token, res.user);
    } catch {
      clearTokens();
    }
  }

  function logout() {
    clearTokens();
  }

  return { accessToken, user, error, loading, isLoggedIn, istAdmin, mussPasswortAendern, login, applyTokens, refresh, logout };
});
